import { ActionIcon, Grid, Tooltip } from '@mantine/core';
import { IconClock, IconDots, IconLock, IconLockOpen, IconRefreshAlert } from '@tabler/icons-react';
import { ContextMenuItemOptions, useContextMenu } from 'mantine-contextmenu';
import * as React from 'react';
import { Show } from '../../../../../react-commons/components/control-flow/Show';
import { LabelNode } from '../../../../../react-commons/components/inputs/getLabelNode';
import { OpenModal } from '../../../../../react-commons/utils/modals/openModal';
import { ControlledInputs } from '../../../../../react-commons/utils/read-only-inputs/controlledInputs';
import { PrototypeVersionMappingState } from '../../../resource/enumerations/PrototypeVersionMappingState';
import { ResourceType } from '../../../resource/enumerations/ResourceType';
import { formatEnum } from '../../../resource/enumerations/utils/formatEnum';
import { VersionState } from '../../../resource/enumerations/VersionState';
import { VersionInfo } from '../../../resource/services/versionInfo';
import { getPropertyLabelInfo } from '../../util/utilFunctions';
import { useSystemInformationActions } from '../../../resource/services/systemInformationService';

export const VersionInfoBarDisplay = ({
    isPublished,
    versionInfo,
    mappingState,
    versionId,
    onVersionGraphClick,
    customButtonsSize,
    CustomButtons,
    CustomMenuItems,
    closeAction,
    openAction,
    lockAction,
    publishMappingAction,
    lockMappingAction,
    openMappingAction,
    variantAndMappingOnly,
    versionOnly,
    versionGraphButtonTooltip,
    versionInfoPropertyIdentifier,
    versionResourceType,
    mappingStatePropertyIdentifier,
    mappingResourceType,
    architectureCheckResultButton,
    versionStateLabel,
    labelsToTooltips,
    titleToShow,
    customButtonIsIcon,
}: {
    isPublished: boolean;
    versionInfo: VersionInfo | null;
    mappingState?: PrototypeVersionMappingState | null;
    versionId: string;
    onVersionGraphClick?: () => void;
    CustomButtons?: React.ReactElement;
    customButtonIsIcon: boolean;
    customButtonsSize?: number;
    CustomMenuItems?: ContextMenuItemOptions[];
    closeAction?: (versionId: string) => void;
    openAction?: (versionId: string) => void;
    lockAction?: (versionId: string) => void;
    publishMappingAction?: () => void;
    lockMappingAction?: () => void;
    openMappingAction?: () => void;
    variantAndMappingOnly?: boolean;
    versionOnly?: boolean;
    versionGraphButtonTooltip?: string;
    versionInfoPropertyIdentifier: string;
    versionResourceType: ResourceType;
    architectureCheckResultButton?: React.ReactNode;
    mappingStatePropertyIdentifier?: string;
    mappingResourceType?: ResourceType;
    versionStateLabel?: string;
    titleToShow?: string;
    labelsToTooltips?: boolean;
}) => {
    const { showContextMenu } = useContextMenu();

    const systemInformationActions = useSystemInformationActions();
    const versionPropertyDetails = systemInformationActions.useGetPropertyDetails(
        versionInfoPropertyIdentifier,
        versionResourceType
    );
    const mappingPropertyDetails =
        mappingStatePropertyIdentifier && mappingResourceType ?
            systemInformationActions.useGetPropertyDetails(mappingStatePropertyIdentifier, mappingResourceType)
        :   undefined;

    const openCloseConfirmDialog = () => {
        if (closeAction) {
            OpenModal.AlertModal(
                'Close Version',
                'Making changes to a closed version is not possible. Are you sure you want to close this version?',
                () => closeAction(versionId)
            );
        }
    };

    const openOpenConfirmDialog = () => {
        if (openAction) {
            OpenModal.AlertModal('Open Version', `Are you sure you want to open this version?`, () =>
                openAction(versionId)
            );
        }
    };

    const openLockConfirmDialog = () => {
        if (lockAction) {
            OpenModal.AlertModal('Lock Version', `Are you sure you want to lock this version?`, () =>
                lockAction(versionId)
            );
        }
    };

    const openPublishMappingConfirmDialog = () => {
        if (publishMappingAction) {
            OpenModal.AlertModal(
                'Publish Mapping',
                `Making changes to a published mapped version is not possible. Are you sure you want to publish this mapping?`,
                () => publishMappingAction()
            );
        }
    };

    const openLockMappingConfirmDialog = () => {
        if (lockMappingAction) {
            OpenModal.AlertModal('Lock Mapping', `Are you sure you want to lock this mapping?`, () =>
                lockMappingAction()
            );
        }
    };

    const openOpenMappingConfirmDialog = () => {
        if (openMappingAction) {
            OpenModal.AlertModal('Open Mapping', `Are you sure you want to open this mapping?`, () =>
                openMappingAction()
            );
        }
    };

    const contextMenuItems = () => {
        const getVersionStateItems = (): ContextMenuItemOptions[] => {
            if (versionInfo && versionInfo.versionState !== VersionState.CLOSED) {
                if (versionInfo?.versionState === VersionState.LOCKED) {
                    return [
                        {
                            key: 'open',
                            icon: <IconLockOpen />,
                            title: 'Open',
                            onClick: () => {
                                openOpenConfirmDialog();
                            },
                        },
                        {
                            key: 'close',
                            icon: <IconRefreshAlert color={'var(--mantine-color-base-button)'} />,
                            title: 'Close',
                            onClick: () => {
                                openCloseConfirmDialog();
                            },
                        },
                    ];
                } else if (versionInfo?.versionState === VersionState.OPEN) {
                    return [
                        {
                            key: 'lock',
                            icon: <IconLock color={'var(--mantine-color-base-button)'} />,
                            title: 'Lock',
                            onClick: () => {
                                openLockConfirmDialog();
                            },
                        },
                        {
                            key: 'close',
                            icon: <IconRefreshAlert color={'var(--mantine-color-base-button)'} />,
                            title: 'Close',
                            onClick: () => {
                                openCloseConfirmDialog();
                            },
                        },
                    ];
                }
            }

            return [];
        };

        const getMappingStateItems = (): ContextMenuItemOptions[] => {
            if (mappingState && mappingState !== PrototypeVersionMappingState.PUBLISHED) {
                if (mappingState === PrototypeVersionMappingState.PUBLISHED_IN_PREVIOUS_VERSION) {
                    return [
                        {
                            key: 'open',
                            icon: <IconLockOpen color={'var(--mantine-color-base-button)'} />,
                            title: 'Open',
                            onClick: () => {
                                openOpenMappingConfirmDialog();
                            },
                        },
                    ];
                } else if (mappingState === PrototypeVersionMappingState.OPEN) {
                    return [
                        {
                            key: 'lock',
                            icon: <IconLock color={'var(--mantine-color-base-button)'} />,
                            title: 'Lock',
                            onClick: () => {
                                openLockMappingConfirmDialog();
                            },
                        },
                        {
                            key: 'publish',
                            icon: <IconRefreshAlert color={'var(--mantine-color-base-button)'} />,
                            title: 'Publish',
                            onClick: () => {
                                openPublishMappingConfirmDialog();
                            },
                        },
                    ];
                } else if (mappingState === PrototypeVersionMappingState.LOCKED) {
                    return [
                        {
                            key: 'open',
                            icon: <IconLockOpen color={'var(--mantine-color-base-button)'} />,
                            title: 'Open',
                            onClick: () => {
                                openOpenMappingConfirmDialog();
                            },
                        },
                        {
                            key: 'publish',
                            icon: <IconRefreshAlert color={'var(--mantine-color-base-button)'} />,
                            title: 'Publish',
                            onClick: () => {
                                openPublishMappingConfirmDialog();
                            },
                        },
                    ];
                }
            }

            return [];
        };

        const getCustomMenuItems = (): ContextMenuItemOptions[] => {
            if (onVersionGraphClick && CustomMenuItems) {
                return [
                    {
                        key: 'version_history',
                        icon: <IconClock />,
                        title: 'Version History',
                        onClick: () => {
                            if (onVersionGraphClick) {
                                onVersionGraphClick();
                            }
                        },
                    },
                    ...CustomMenuItems,
                ];
            }

            return CustomMenuItems ? CustomMenuItems : [];
        };

        return { getVersionStateItems, getMappingStateItems, getCustomMenuItems };
    };

    const showVersionStateTextField = () => {
        return !!(!variantAndMappingOnly && versionInfo);
    };

    const showMappingStateTextField = () => {
        return !!mappingState && !versionOnly;
    };

    if (!versionInfo && !variantAndMappingOnly) {
        return (
            <Grid>
                <Grid.Col span={12}>
                    <ControlledInputs.String
                        style={{
                            borderRadius: '50',
                            backgroundColor: 'lightgray',
                        }}
                        value={'No Version Attached'}
                        nullable={false}
                        readOnly={true}
                    />
                </Grid.Col>
            </Grid>
        );
    }

    const getVersionGraphButton = () => {
        if (!CustomMenuItems && onVersionGraphClick) {
            if (versionGraphButtonTooltip) {
                return (
                    <div style={{ paddingTop: !labelsToTooltips ? '32px' : '8px' }}>
                        <Tooltip label={versionGraphButtonTooltip}>
                            <ActionIcon
                                id={'time-button'}
                                size={'sm'}
                                variant={'transparent'}
                                onClick={() => {
                                    if (onVersionGraphClick) {
                                        onVersionGraphClick();
                                    }
                                }}
                            >
                                <IconClock color={'var(--mantine-color-base-button)'} />
                            </ActionIcon>
                        </Tooltip>
                    </div>
                );
            } else {
                return (
                    <div style={{ paddingTop: !labelsToTooltips ? '32px' : '8px' }}>
                        <ActionIcon
                            id={'time-button'}
                            size={'sm'}
                            variant={'transparent'}
                            onClick={() => {
                                if (onVersionGraphClick) {
                                    onVersionGraphClick();
                                }
                            }}
                        >
                            <IconClock color={'var(--mantine-color-base-button)'} />
                        </ActionIcon>
                    </div>
                );
            }
        } else {
            return <></>;
        }
    };

    const getLabelNode = (label: string, labelInfo: React.ReactElement | undefined) => {
        return <LabelNode label={label} labelInfo={labelInfo} />;
    };

    if (titleToShow) {
        return (
            <Grid>
                <Grid.Col span={11}>
                    <ControlledInputs.String value={titleToShow} readOnly nullable={false} label={'Baseline'} />
                </Grid.Col>
                <Grid.Col span={1}>{getVersionGraphButton()}</Grid.Col>
            </Grid>
        );
    }

    return (
        <Grid>
            <Show when={!variantAndMappingOnly}>
                <Grid.Col span={showMappingStateTextField() && showVersionStateTextField() ? 3 : 4}>
                    <Show
                        when={labelsToTooltips}
                        fallback={
                            <ControlledInputs.String
                                w={'100%'}
                                label={getLabelNode('Version Number', getPropertyLabelInfo(versionPropertyDetails))}
                                value={versionInfo?.versionNumber ?? ''}
                                nullable={false}
                                readOnly
                            />
                        }
                    >
                        <Tooltip label={'Version Number'}>
                            <div>
                                <ControlledInputs.String
                                    w={'100%'}
                                    value={versionInfo?.versionNumber ?? ''}
                                    nullable={false}
                                    readOnly
                                />
                            </div>
                        </Tooltip>
                    </Show>
                </Grid.Col>
            </Show>
            <Show when={showVersionStateTextField()}>
                <Grid.Col
                    span={
                        showMappingStateTextField() ? 3
                        : (
                            customButtonsSize ||
                            (CustomButtons ?
                                ((CustomButtons.props as { children?: Array<unknown> }).children?.length ?? 0)
                            :   0) > 1
                        ) ?
                            3
                        :   5
                    }
                >
                    <Show
                        when={labelsToTooltips}
                        fallback={
                            <ControlledInputs.String
                                w={'100%'}
                                label={getLabelNode(
                                    versionStateLabel ?? (showMappingStateTextField() ? 'Version State' : 'State'),
                                    getPropertyLabelInfo(versionPropertyDetails)
                                )}
                                value={
                                    isPublished ?
                                        `${formatEnum(VersionState, versionInfo?.versionState)} (Published)`
                                    :   formatEnum(VersionState, versionInfo?.versionState)
                                }
                                endingIcon={
                                    versionInfo?.versionState !== VersionState.CLOSED && (
                                        <ActionIcon
                                            size={'sm'}
                                            variant={'transparent'}
                                            onClick={(event) => {
                                                showContextMenu(contextMenuItems().getVersionStateItems())(event);
                                            }}
                                            p={2}
                                        >
                                            <IconDots color={'var(--mantine-color-base-button)'} />
                                        </ActionIcon>
                                    )
                                }
                                readOnly
                                nullable={false}
                            />
                        }
                    >
                        <Tooltip label={versionStateLabel ?? (showMappingStateTextField() ? 'Version State' : 'State')}>
                            <div>
                                <ControlledInputs.String
                                    w={'100%'}
                                    value={formatEnum(VersionState, versionInfo?.versionState)}
                                    endingIcon={
                                        versionInfo?.versionState !== VersionState.CLOSED && (
                                            <ActionIcon
                                                size={'sm'}
                                                variant={'transparent'}
                                                onClick={(event) => {
                                                    showContextMenu(contextMenuItems().getVersionStateItems())(event);
                                                }}
                                                p={2}
                                            >
                                                <IconDots color={'var(--mantine-color-base-button)'} />
                                            </ActionIcon>
                                        )
                                    }
                                    readOnly
                                    nullable={false}
                                />
                            </div>
                        </Tooltip>
                    </Show>
                </Grid.Col>
            </Show>
            <Show when={showMappingStateTextField()}>
                <Grid.Col
                    span={
                        variantAndMappingOnly ? 5
                        : showVersionStateTextField() ?
                            4
                        :   5
                    }
                >
                    <Show
                        when={labelsToTooltips}
                        fallback={
                            <ControlledInputs.String
                                style={{ width: '100%' }}
                                label={getLabelNode(
                                    showVersionStateTextField() ? 'Mapping State' : 'State',
                                    getPropertyLabelInfo(mappingPropertyDetails)
                                )}
                                readOnly
                                nullable={false}
                                value={formatEnum(PrototypeVersionMappingState, mappingState)}
                                endingIcon={
                                    mappingState !== PrototypeVersionMappingState.PUBLISHED ?
                                        <ActionIcon
                                            variant={'transparent'}
                                            size={'sm'}
                                            onClick={(event) =>
                                                showContextMenu(contextMenuItems().getMappingStateItems())(event)
                                            }
                                            p={2}
                                        >
                                            <IconDots color={'var(--mantine-color-base-button)'} />
                                        </ActionIcon>
                                    :   architectureCheckResultButton
                                }
                            />
                        }
                    >
                        <Tooltip label={showVersionStateTextField() ? 'Mapping State' : 'State'}>
                            <div>
                                <ControlledInputs.String
                                    style={{ width: '100%' }}
                                    readOnly
                                    nullable={false}
                                    value={formatEnum(PrototypeVersionMappingState, mappingState)}
                                    endingIcon={
                                        mappingState !== PrototypeVersionMappingState.PUBLISHED ?
                                            <ActionIcon
                                                variant={'transparent'}
                                                size={'sm'}
                                                onClick={(event) =>
                                                    showContextMenu(contextMenuItems().getMappingStateItems())(event)
                                                }
                                                p={2}
                                            >
                                                <IconDots color={'var(--mantine-color-base-button)'} />
                                            </ActionIcon>
                                        :   architectureCheckResultButton
                                    }
                                />
                            </div>
                        </Tooltip>
                    </Show>
                </Grid.Col>
            </Show>
            <Grid.Col
                span={
                    (
                        (customButtonsSize ??
                        (CustomButtons ?
                            ((CustomButtons.props as { children?: Array<unknown> }).children?.length ?? 0)
                        :   0) > 1)
                    ) ?
                        5
                    :   2
                }
                style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '5px',
                }}
            >
                {CustomButtons && customButtonIsIcon && (
                    <div style={{ paddingTop: customButtonIsIcon ? '30px' : '' }}>{CustomButtons}</div>
                )}
                {CustomButtons && !customButtonIsIcon && <>{CustomButtons}</>}
                {getVersionGraphButton()}
                <Show when={CustomMenuItems}>
                    <ActionIcon
                        size={'sm'}
                        variant={'transparent'}
                        onClick={(event) => showContextMenu(contextMenuItems().getCustomMenuItems())(event)}
                    >
                        <IconDots color={'var(--mantine-color-base-button)'} />
                    </ActionIcon>
                </Show>
            </Grid.Col>
        </Grid>
    );
};
